/**
 * Helper functions for consistent feature gating across the platform
 * Implements expert recommendation for centralized feature checking
 */

import { EMBEDDED_APPS, getEmbeddedApp } from '../config/embeddedApps';

/**
 * Check if a community feature is enabled for a center
 * @param {Object} config - The center configuration object
 * @param {string} feature - The feature to check ('announcements', 'calendar', 'qa', 'services')
 * @returns {boolean} - Whether the feature is enabled
 */
export const isCommunityFeatureEnabled = (config, feature) => {
  // Master switch check first - if portal is explicitly disabled, no features show
  if (config?.community_portal_enabled === false) {
    return false;
  }
  
  // Embedded external apps (Library, Housing, ...) are registry-driven rather
  // than hardcoded: each is gated by community_features.<id>.enabled, so a new
  // app needs an entry in config/embeddedApps.js and nothing here.
  if (getEmbeddedApp(feature)) {
    return config?.community_features?.[feature]?.enabled === true;
  }

  // Check individual features with proper fallbacks
  switch(feature) {
    case 'announcements':
      // Support both nested object and boolean formats
      return config?.community_features?.announcements?.enabled === true || 
             config?.community_features?.announcements === true;
             
    case 'calendar':
      return config?.community_features?.calendar?.enabled === true ||
             config?.community_features?.calendar === true ||
             config?.islamic_occasions_enabled === true;
             
    case 'qa':
      return config?.community_features?.qa?.enabled === true ||
             config?.community_features?.qa === true;
             
    case 'services':
      // Check both top-level flag (database) and nested (GitHub config)
      return config?.community_services_enabled === true ||
             config?.community_features?.services?.enabled === true;

    case 'tax_docs':
      // Requires Services to be enabled AND a tax receipts sheet configured
      return (config?.community_services_enabled === true ||
              config?.community_features?.services?.enabled === true) &&
             !!config?.tax_receipts_sheet_id;

    case 'islamic_occasions':
      return config?.islamic_occasions_enabled === true;

    default:
      return false;
  }
};

/**
 * Resolve the URL an embedded app should load for the current center.
 *
 * Centers may point an app somewhere else by setting
 * community_features.<id>.url (white-labeling, regional variants, a staging
 * build); otherwise the registry default is used. Returns null for ids that
 * are not in the registry so callers can fail loudly rather than iframe
 * `undefined`.
 */
export const getEmbeddedAppUrl = (config, appId) => {
  const app = getEmbeddedApp(appId);
  if (!app) return null;
  return config?.community_features?.[appId]?.url || app.defaultUrl;
};

/**
 * Registry entries for every embedded app this center has switched on, in
 * registry order. Navigation surfaces render straight from this.
 */
export const getEnabledEmbeddedApps = (config) =>
  EMBEDDED_APPS.filter((app) => isCommunityFeatureEnabled(config, app.id));

/**
 * Check if any community feature is enabled (for showing Community menu)
 * @param {Object} config - The center configuration object
 * @returns {boolean} - Whether any community feature is enabled
 */
export const hasAnyCommunityFeature = (config) => {
  // If portal is explicitly disabled, return false
  if (config?.community_portal_enabled === false) {
    return false;
  }
  
  // Check if any individual feature is enabled
  return isCommunityFeatureEnabled(config, 'announcements') ||
         isCommunityFeatureEnabled(config, 'calendar') ||
         isCommunityFeatureEnabled(config, 'qa') ||
         isCommunityFeatureEnabled(config, 'services');
};

/**
 * Get list of enabled community features for navigation
 * @param {Object} config - The center configuration object
 * @returns {Array} - Array of enabled feature objects with id, label, and path
 */
export const getEnabledCommunityFeatures = (config) => {
  const features = [];
  
  // If portal is explicitly disabled, return empty array
  if (config?.community_portal_enabled === false) {
    return features;
  }
  
  if (isCommunityFeatureEnabled(config, 'announcements')) {
    features.push({ 
      id: 'announcements', 
      label: 'Announcements', 
      path: '/community/announcements' 
    });
  }
  
  if (isCommunityFeatureEnabled(config, 'calendar')) {
    features.push({ 
      id: 'calendar', 
      label: 'Calendar', 
      path: '/community/calendar' 
    });
  }
  
  if (isCommunityFeatureEnabled(config, 'qa')) {
    features.push({ 
      id: 'qa', 
      label: 'Q&A', 
      path: '/community/qa' 
    });
  }
  
  if (isCommunityFeatureEnabled(config, 'services')) {
    features.push({ 
      id: 'services', 
      label: 'Community Services', 
      path: '/community/services' 
    });
  }
  
  return features;
};

const featureHelpers = {
  isCommunityFeatureEnabled,
  hasAnyCommunityFeature,
  getEnabledCommunityFeatures,
  getEmbeddedAppUrl,
  getEnabledEmbeddedApps
};

export default featureHelpers;